import { useEffect, useState } from "react";
import { CheckCircle2, LoaderCircle, LockKeyhole, MailCheck, Printer, UserRound, XCircle } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import DigitalTicket from "@/components/DigitalTicket";
import { api } from "@/lib/api";
import { getCustomerToken, saveCustomerToken } from "@/lib/customerAuth";

export default function TicketResult() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [hasAccount, setHasAccount] = useState(Boolean(getCustomerToken()));
  const [passwords, setPasswords] = useState({ password: "", confirm: "" });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [accountError, setAccountError] = useState("");

  useEffect(() => {
    if (!sessionId) {
      setError("No se encontró la referencia de la compra.");
      return undefined;
    }
    let attempts = 0;
    let timer;
    const load = async () => {
      try {
        const response = await api.get(`/tickets/checkout-session/${encodeURIComponent(sessionId)}`);
        setOrder(response.data);
        if (response.data.status === "pending" && attempts < 15) {
          attempts += 1;
          timer = window.setTimeout(load, 2000);
        }
      } catch (requestError) {
        setError(requestError.response?.data?.detail || "No se pudo consultar la compra.");
      }
    };
    load();
    return () => window.clearTimeout(timer);
  }, [sessionId]);

  const createAccount = async (event) => {
    event.preventDefault();
    setAccountError("");
    if (passwords.password.length < 8) {
      setAccountError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (passwords.password !== passwords.confirm) {
      setAccountError("Las contraseñas no coinciden.");
      return;
    }
    setCreating(true);
    try {
      const response = await api.post("/auth/create-from-purchase", {
        session_id: sessionId,
        password: passwords.password,
      });
      saveCustomerToken(response.data.token);
      setHasAccount(true);
    } catch (requestError) {
      setAccountError(requestError.response?.data?.detail || "No se pudo crear la cuenta.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="section-pad">
      <div className="container-cg max-w-5xl">
        {error ? (
          <div className="card-cg p-8 text-center">
            <XCircle className="mx-auto text-red-700" size={36} />
            <h1 className="font-serif-cg text-3xl mt-4">No pudimos mostrar los boletos</h1>
            <p className="text-sm text-[var(--brand-muted)] mt-3">{error}</p>
            <Link to="/boletos" className="btn-primary-cg mt-6">Volver a boletos</Link>
          </div>
        ) : !order || order.status === "pending" ? (
          <div className="card-cg p-10 text-center">
            <LoaderCircle className="animate-spin mx-auto text-[var(--brand-primary)]" size={36} />
            <h1 className="font-serif-cg text-3xl mt-5">Confirmando tu pago</h1>
            <p className="text-sm text-[var(--brand-muted)] mt-3">No cierres esta página. Tus boletos aparecerán en cuanto Stripe confirme el pago.</p>
          </div>
        ) : order.status !== "paid" ? (
          <div className="card-cg p-10 text-center">
            <XCircle className="mx-auto text-amber-700" size={36} />
            <h1 className="font-serif-cg text-3xl mt-5">Pago no completado</h1>
            <p className="text-sm text-[var(--brand-muted)] mt-3">Estado de la orden: {order.status}</p>
            <Link to="/boletos" className="btn-primary-cg mt-6">Intentar nuevamente</Link>
          </div>
        ) : (
          <>
            <section className="rounded-lg bg-[var(--brand-primary)] px-6 py-8 md:px-10 md:py-10 text-white">
              <div className="overline text-[#e4d39f] flex items-center gap-2"><CheckCircle2 size={17} /> Pago confirmado</div>
              <h1 className="font-serif-cg text-4xl md:text-5xl mt-3">Gracias por tu compra, {order.buyer_name}.</h1>
              <p className="mt-3 text-sm text-white/80 max-w-2xl">Tus {order.quantity} {order.quantity === 1 ? "boleto está listo" : "boletos están listos"}. Puedes presentarlos desde el teléfono o descargarlos.</p>
              <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 border-t border-white/20 pt-5 text-sm">
                <div><span className="block text-[10px] tracking-[0.16em] uppercase text-[#e4d39f]">Boletos</span>${(order.subtotal_mxn ?? order.amount_mxn).toFixed(2)} MXN{order.discount_mxn > 0 ? <><br /><span className="text-[#e4d39f]">-{order.discount_mxn.toFixed(2)} · {order.discount_code}</span></> : null}</div>
                <div><span className="block text-[10px] tracking-[0.16em] uppercase text-[#e4d39f]">Servicio</span>${(order.service_fee_mxn ?? 0).toFixed(2)} MXN<br /><strong>Total ${order.amount_mxn.toFixed(2)}</strong></div>
                <div><span className="block text-[10px] tracking-[0.16em] uppercase text-[#e4d39f]">Correo</span><span className="break-all">{order.buyer_email}</span></div>
                <div><span className="block text-[10px] tracking-[0.16em] uppercase text-[#e4d39f]">Orden</span><span className="font-mono text-xs break-all">{order.order_id}</span></div>
              </div>
            </section>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border border-[var(--brand-border)] bg-white px-5 py-4">
              <div className="flex items-center gap-3">
                <MailCheck className="text-[var(--brand-primary)]" size={22} />
                <div><strong className="text-sm">{order.email_status === "sent" ? "Confirmación enviada" : "Tus QR están disponibles aquí"}</strong><p className="text-xs text-[var(--brand-muted)] mt-0.5">{order.email_status === "sent" ? `Enviamos una copia a ${order.buyer_email}.` : "Conserva esta página mientras se procesa el correo."}</p></div>
              </div>
              <button type="button" onClick={() => window.print()} className="btn-secondary-cg"><Printer size={16} /> Imprimir</button>
            </div>

            {!hasAccount ? (
              <section className="mt-6 grid md:grid-cols-[0.85fr_1.15fr] border border-[var(--brand-border)] bg-[#f3f0e5]">
                <div className="p-6 md:p-8">
                  <UserRound size={26} className="text-[var(--brand-primary)]" />
                  <h2 className="font-serif-cg text-2xl mt-4">Guarda tus boletos</h2>
                  <p className="text-sm text-[var(--brand-muted)] mt-2 leading-relaxed">Crea tu cuenta con el correo de esta compra para consultar y descargar tus boletos desde cualquier dispositivo.</p>
                </div>
                <form onSubmit={createAccount} className="border-t md:border-t-0 md:border-l border-[var(--brand-border)] bg-white p-6 md:p-8 space-y-3">
                  <label className="block"><span className="overline text-[var(--brand-muted)]">Crear contraseña</span><input required type="password" minLength={8} autoComplete="new-password" value={passwords.password} onChange={(event) => setPasswords({ ...passwords, password: event.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" /></label>
                  <label className="block"><span className="overline text-[var(--brand-muted)]">Confirmar contraseña</span><input required type="password" minLength={8} autoComplete="new-password" value={passwords.confirm} onChange={(event) => setPasswords({ ...passwords, confirm: event.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" /></label>
                  {accountError ? <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700">{accountError} {accountError.includes("Ya existe") ? <Link className="underline font-semibold" to="/mi-cuenta">Iniciar sesión</Link> : null}</div> : null}
                  <button disabled={creating} className="btn-primary-cg w-full justify-center disabled:opacity-50"><LockKeyhole size={16} /> {creating ? "Creando..." : "Crear cuenta y guardar"}</button>
                </form>
              </section>
            ) : (
              <div className="mt-6 border border-[var(--brand-border)] bg-[#f3f0e5] p-5 flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm"><strong>Boletos guardados en tu cuenta.</strong> Puedes consultarlos después desde cualquier dispositivo.</p>
                <Link to="/mi-cuenta" className="btn-primary-cg">Abrir Mi cuenta</Link>
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-6 mt-8">
              {order.tickets.map((ticket) => <DigitalTicket key={ticket.id} ticket={ticket} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
